import { select, Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { AppState } from '../store/app.state';
import { api } from '@consts/url.const';
import {
  ACCESS_TOKEN_KEY,
  AUTH_PROVIDER_ID_KEY,
  AuthenticationService,
  EXPIRES_IN_KEY,
  ID_TOKEN_KEY,
} from '../authentication/authentication.service';
import { SharedSelectors } from '@states/shared/shared.selector-types';
import { PublicRoutesRedirect, routerSegments } from '@consts/routes';
import { Auth0Service } from '../authentication/auth0.service';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { LocalStorageService } from '@ui';
import { UserService } from '../user/user.service';
import { Auth0 } from '../authentication/auth0.model';
import { AuthSessionService } from '../authentication/auth-session.service';
import { UniversalLoginService } from '../authentication/universal-login.service';
import {
  AuthRedirectLoopService,
  buildAccessDeniedRedirectUrl,
  clearCurrentUrlQueryParams,
} from '@auth';
import { AppUpdateService } from './app-update.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const DOCS_REDIRECT_PATH_KEY = 'docsRedirectPath';

@Injectable()
export class AppConfig {
  constructor(
    private store$: Store<AppState>,
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private auth0Service: Auth0Service,
    private userService: UserService,
    private localStorageService: LocalStorageService,
    private authSessionService: AuthSessionService,
    private universalLoginService: UniversalLoginService,
    private authRedirectLoopService: AuthRedirectLoopService,
    private appUpdateService: AppUpdateService,
    private destroyRef: DestroyRef,
  ) {
  }

  public async load(): Promise<any> {
    try {
      return await new Promise(async resolve => {
        const url = new URL(window.location.href);
        const docsLocationPath = url.searchParams.get('location');
        if (docsLocationPath) {
          sessionStorage.setItem(DOCS_REDIRECT_PATH_KEY, docsLocationPath);
        }
        const isPostAppUpdate = this.appUpdateService.isPostUpdateNavigation(url);
        const code = url.searchParams.get('code');
        const error = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');
        const success = url.searchParams.get('success');
        const pathname = url.pathname;
        const isLogoutRoute = pathname === `/${routerSegments.logout}` || pathname === `/${routerSegments.logout}/`;
        let redirectUrl = url.searchParams.get('redirectUrl');
        if (isLogoutRoute) {
          this.authRedirectLoopService.reset();
          this.authenticationService.handleLogoutCallback();
          resolve(true);
          return;
        }
        if (pathname === `/${routerSegments.emailVerification}` && success === 'true') {
          clearCurrentUrlQueryParams(url);
          this.universalLoginService.redirectToLogin(window.location.origin);
          resolve(true);
          return;
        }
        if (error === 'access_denied') {
          const accessDeniedRedirectUrl = buildAccessDeniedRedirectUrl({
            error,
            errorDescription,
            resetPasswordPath: `/${routerSegments.auth}/${routerSegments.resetPassword}`,
            emailVerificationPath: `/${routerSegments.public}/${routerSegments.emailVerification}`,
            accessDeniedPath: `/${routerSegments.public}/${routerSegments.accessDenied}`,
          });

          if (accessDeniedRedirectUrl) {
            clearCurrentUrlQueryParams(url);
            window.location.href = accessDeniedRedirectUrl;
            resolve(true);
            return;
          }

          const result = await firstValueFrom(this.auth0Service.logoutPublic());
          window.location.href = result.logoutUrl;
          resolve(true);
          return;
        }

        if (pathname?.indexOf(routerSegments.support) > -1) {
          redirectUrl = routerSegments.support;
        }

        if (code && this.isRootRoute(pathname)) {
          const authResult = await firstValueFrom(this.userService.loginByUniversalLoginCode(code, url.origin));
          if (authResult && authResult.access_token && authResult.id_token) {
            this.authRedirectLoopService.reset();
            const transformedAuthResult: Auth0.AuthenticationResponse = {
              ...authResult,
              expiresIn: authResult.expires_in,
              idTokenPayload: undefined,
              state: '',
              tokenType: '',
              idToken: authResult.id_token,
              accessToken: authResult.access_token,
            };
            this.auth0Service.afterTokenReceivedSuccess(transformedAuthResult);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            url.searchParams.delete('orgId');
            this.appUpdateService.clearPostUpdateNavigationState(url);
            window.history.replaceState({}, '', url.toString());
          }
        } else {
          const pathname = url.pathname?.split('/');

          if (window?.location?.host?.includes('dev.lumix.ai')) {
            const newUrl = window.location.href.replace('dev.lumix.ai', 'dev.lumana.ai');
            window.location.href = newUrl;
            return;
          }

          if (window?.location?.host?.includes('staging.lumix.ai')) {
            const newUrl = window.location.href.replace('staging.lumix.ai', 'staging.lumana.ai');
            window.location.href = newUrl;
            return;
          }

          if (window?.location?.host?.includes('app.lumix.ai')) {
            const newUrl = window.location.href.replace('app.lumix.ai', 'app.lumana.ai');
            window.location.href = newUrl;
            return;
          }


          if (code?.toString() === '403') {
            resolve(true);
            return;
          }

          let isLoggedIn = this.auth0Service.isLoggedIn();

          if (!isLoggedIn) {
            isLoggedIn = await this.authSessionService.ensureValidSession({
              allowBackgroundRefresh: false,
              isSilentRefresh: false,
            });
          }

          if (!isLoggedIn) {
            if (pathname?.length >= 2 && PublicRoutesRedirect.includes(pathname[1])) {
              resolve(true);
            } else {
              this.authenticationService.clear();
              if (docsLocationPath) {
                sessionStorage.setItem(DOCS_REDIRECT_PATH_KEY, docsLocationPath);
              }
              const targetUrl = isPostAppUpdate
                ? window.location.origin
                : redirectUrl
                  ? new URL(redirectUrl, window.location.origin).toString()
                  : window.location.href;
              this.universalLoginService.redirectToLogin(targetUrl);
              resolve(true);
            }
          } else {
            this.authRedirectLoopService.reset();
            if (isPostAppUpdate) {
              this.appUpdateService.clearPostUpdateNavigationState(url);
            }
            const accessToken = this.localStorageService.getItemWithCheck(ACCESS_TOKEN_KEY);
            const expiresAt = this.localStorageService.getItemWithCheck(EXPIRES_IN_KEY);
            const idToken = this.localStorageService.getItemWithCheck(ID_TOKEN_KEY);
            const authProviderId = this.localStorageService.getItemWithCheck(AUTH_PROVIDER_ID_KEY);

            if (accessToken && idToken && expiresAt) {
              this.store$.dispatch(
                AuthenticationActions.Authenticated({
                  auth: {
                    idToken,
                    accessToken,
                    expiresAt,
                    authProviderId,
                  },
                }),
              );
            }
          }
        }

        this.store$.pipe(
          select(SharedSelectors.selectIsApplicationLoaded))
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(x => !!x),
          )
          .subscribe(_ => {
            const docsRedirectPath = sessionStorage.getItem(DOCS_REDIRECT_PATH_KEY);
            if (docsRedirectPath && this.auth0Service.isLoggedIn()) {
              sessionStorage.removeItem(DOCS_REDIRECT_PATH_KEY);
              url.searchParams.delete('location');
              window.history.replaceState({}, '', url.toString());
              this.redirectToDocsPage(docsRedirectPath).finally(() => resolve(true));
              return;
            }
            resolve(true);
          },
          );
      });
    } catch (e) {
      this.authenticationService.logout();
    }
  }

  private async redirectToDocsPage(locationPath: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ url: string }>(api.support.gitbookAuthUrl),
      );
      const authUrl = new URL('https://' + response.url);
      authUrl.pathname = locationPath.startsWith('/') ? locationPath : '/' + locationPath;
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Failed to redirect to docs page', error);
    }
  }

  private isRootRoute(pathname: string): boolean {
    return pathname === '' || pathname === '/';
  }
}
